import React from 'react';
import { Eye, EyeOff, Mail, Lock, KeyRound } from 'lucide-react';
import { DialogManager } from '../fasades/dialogmanager';
import { IoClose } from "react-icons/io5";
import { RoundButton } from '../chatbot/components/roundbutton';
import { Users } from '../fasades/users';
import { ApiError } from '../chatbot/api/llmapi';
import { ToastManager, TOAST_TYPES } from '../fasades/toastmanager';
import './authorizedialog.css';
import 'altcha';

type ErrorInfo = {
    password?       : string;
    email?          : string;
    repeatPassword? : string;
    agreeToTerms?   : string;
    code?           : string;
    criticalError?  : string;
};

enum DialogType {
    Login,
    Verify,
    CreateAccount,
    ForgotPassword,
    ChangePassword
};

type SignInInfo = {
    type           : DialogType,
    code           : string,
    email          : string,
    password       : string,
    payload        : string,
    repeatPassword : string,
    showPassword   : boolean,
    showRepeat     : boolean,
    agreeToTerms   : boolean,
    errors         : ErrorInfo,
    token?         : string
};

type AutoProps = {
    token? : string;
};


export class AuthorizeDialog extends React.Component<AutoProps, SignInInfo>
{
    dialogRef = React.createRef<HTMLDialogElement>();
    altchaRef = React.createRef<any>();

    constructor(props : AutoProps) {
        super(props);

        let type = DialogType.Login;
        if (this.props.token) {
            type = DialogType.ChangePassword;
        }

        this.state = {
            type           : type,
            code           : '',
            email          : '',
            password       : '',
            repeatPassword : '',
            payload        : '',
            showPassword   : false,
            showRepeat     : false,
            agreeToTerms   : false,
            errors         : {},
            token          : this.props.token
        };
    }

    componentDidMount() 
    {
        if (this.dialogRef.current) {
            this.dialogRef.current.showModal();
        }
    }

    getSubmitText() : string
    {
        switch(this.state.type) {
            case DialogType.Login :
                return "Sign In";
            case DialogType.Verify :
                return "Verify";
            case DialogType.CreateAccount :
                return "Create Account";
            case DialogType.ForgotPassword :
                return "Reset your password";
            case DialogType.ChangePassword :
                return "Change password";
        }
    }

    togglePasswordVisibility = () => {
        this.setState(prevState => ({
            showPassword: !prevState.showPassword
        }));
    };

    toggleRepeatVisibility = () => {
        this.setState(prevState => ({
            showRepeat: !prevState.showRepeat
        }));
    };

    handleInputChange = (e : React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === 'email') {
            this.setState({
              email  : value,
              errors : { ...this.state.errors, email: '' }
            });
        }
        else if (name === 'code') {
            this.setState({
                code : value,
                errors : { ...this.state.errors, code: '' }
            });
        }
        else if (name === 'password') {
            this.setState({
                password : value,
                errors   : { ...this.state.errors, password: '', email : '' }
            });
        }
        else if (name === 'repeatPassword') {
            this.setState({
                repeatPassword : value,
                errors         : { ...this.state.errors, repeatPassword: '' }
            });
        }
    };

    handleClose()
    {
        if (this.dialogRef.current) {
            this.dialogRef.current.close();
        }
        DialogManager.close();
    }

    async handleSubmit(e : React.MouseEvent<HTMLButtonElement>)
    {
        e.preventDefault();
        let errors = this.validateForm();
        this.setState({ errors });

        if (Object.keys(errors).length > 0) {
            return;
        }

        const { type, email, password } = this.state;

        try {
            if (type === DialogType.Login) {
                await Users.login(this.state.email, this.state.password, this.state.payload);
                this.handleClose();
            }
            else if (type === DialogType.Verify) {
                await Users.verify(email, password, this.state.code, this.state.payload);
                ToastManager.addToast("Account verified!", TOAST_TYPES.SUCCESS, 5000);
                this.handleClose();
            }
            else if (type === DialogType.CreateAccount) {
                await Users.createAccount(this.state.email, this.state.password, this.state.payload);
                ToastManager.addToast("Account created! Check your email to verify your email address.", TOAST_TYPES.SUCCESS, 5000);
                this.setState({ type : DialogType.Verify });
            }
            else if (type === DialogType.ForgotPassword) {
                await Users.forgotPassword(this.state.email, this.state.payload);
                ToastManager.addToast("Password reset link sent! Check your email to continue.", TOAST_TYPES.SUCCESS, 20000);
                this.handleClose();
            }
            else if (type === DialogType.ChangePassword) {
                await Users.changePassword(this.state.email, this.state.password, this.props.token!, this.state.payload);
                this.removeTokenParam();
                ToastManager.addToast("Password changed!", TOAST_TYPES.SUCCESS, 5000);
                this.handleClose();
            }
        }
        catch(error) {
            if (error instanceof ApiError) {
                if (error.code === "INCORRECT_CREDENTIALS") {
                    const errors = {
                        password : error.message,
                        email    : error.message
                    };
                    this.setState({errors});
                }
                else if (error.code === "NEED_VERIFICATION") {
                    this.setState({ type : DialogType.Verify });
                }
                else if (error.code === "INCORRECT_EMAIL_CODE") {
                    const errors = {
                        code : error.message
                    }
                    this.setState({errors});
                }
                else if (error.code === "USER_LOCKED") {
                    const errors = {
                        criticalError : error.message
                    }
                    this.setState({errors});
                }
                else if (error.code === "EMAIL_TOKEN_HAS_EXPIRED") {
                    const errors = {
                        criticalError : error.message
                    }
                    this.setState({errors});
                }
                else if (error.code === "EMAIL_ADDRESS_NOT_FOUND") {
                    const errors = {
                        email : error.message
                    }
                    this.setState({errors});
                }
                else if (error.code === "EMAIL_ALREADY_SENT") {
                    const errors = {
                        criticalError : error.message
                    }
                    this.setState({errors});
                }
                else if (error.code === "USER_EXIST") {
                    const errors = {
                        email : error.message
                    }
                    this.setState({errors});
                }
                else if (error.code === "WRONG_EMAIL") {
                    const errors = {
                        email : error.message
                    }
                    this.setState({errors});
                }
                else if (error.code === "YOU_ARE_ROBOT") {
                    if (this.altchaRef.current) {
                        this.altchaRef.current.reset('expired');
                    }
                    this.setState({ payload : '' });
                }
                else {
                    console.error(`ApiError caught. Status: ${error.code}, Code: ${error.message}`);
                }
            }
        }
    }

    handleType(type : DialogType)
    {
        this.setState({
            type : type,
            errors: {}
        });
    };

    render() : React.JSX.Element {
        const { type, code, email, password, repeatPassword, showPassword, showRepeat, errors } = this.state;

        return (
          <dialog ref={this.dialogRef} className="dialogStyle btn">
            <div className="close">
                <RoundButton
                    size={18}
                    description = { false }
                    icon = {IoClose}
                    name="Back to conversation"
                    onClick={ () => this.handleClose() }
                />
            </div>

            { this.renderHeader() }

            <div>
              <div className="signin-field">
                <label htmlFor="email">
                Email Address
                </label>
                <div className="input-with-icons">
                  <Mail className="left" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={this.handleInputChange}
                    className={errors.email ? 'error' : ''}
                    readOnly={ type === DialogType.Verify || this.state.errors.criticalError !== undefined }
                    placeholder="Enter your email"
                  />
                </div>
                {errors.email && (
                  <p>{errors.email}</p>
                )}
              </div>

              { type === DialogType.Verify && (
              <div className="signin-field">
                <label htmlFor="email">
                Enter code from Email
                </label>
                <div className="input-with-icons">
                  <KeyRound className="left" />
                  <input
                    type="text"
                    id="code"
                    name="code"
                    inputMode="numeric" 
                    pattern="\d{8}" 
                    maxLength={8}
                    value={code}
                    onChange={this.handleInputChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="Enter your email"
                  />
                </div>
                {errors.code && (
                  <p>{errors.code}</p>
                )}
              </div>
              )}

              { (type === DialogType.CreateAccount || type === DialogType.Login || type === DialogType.ChangePassword) && errors.criticalError === undefined && (
                this.renderPassword(password, errors.password, showPassword, type === DialogType.Login) 
              )}
              { (errors.criticalError && (
                    <div className="lock-warning">{this.renderText(errors.criticalError)}</div> 
                )
              )}

              { (type === DialogType.CreateAccount  || type === DialogType.ChangePassword) && (errors.criticalError === undefined) && (
              <div className="signin-field">
                  <label htmlFor="repeatPassword">
                    Reenter Password
                  </label>
                  <div className="input-with-icons">
                    <Lock className="left"/>
                    <input
                      type={showRepeat ? 'text' : 'password'}
                      id="repeatPassword"
                      name="repeatPassword"
                      value={repeatPassword}
                      onChange={this.handleInputChange}
                      className={ errors.repeatPassword ? 'error' : ''}
                      placeholder="Reenter your password"
                    />
                    <button
                      tabIndex={-1}
                      type="button"
                      onClick={() => { this.toggleRepeatVisibility() } }
                      className="right"
                    >
                      {showRepeat ? <EyeOff/> : <Eye/>}
                    </button>
                  </div>
                  {errors.repeatPassword && (
                    <p>{ errors.repeatPassword }</p>
                  )}
              </div>
              )}

              { type === DialogType.CreateAccount && errors.criticalError === undefined && (
              <div className="terms-agreement">
                  <label className="terms-checkbox">
                    <input
                      type="checkbox"
                      checked={this.state.agreeToTerms}
                      onChange={() => this.setState(prev => ({ agreeToTerms: !prev.agreeToTerms, errors: { ...prev.errors, agreeToTerms: '' } }))}
                    />
                    <span>
                      I agree to the{' '}
                      <a href="/docs/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                      {' '}and{' '}
                      <a href="/docs/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                    </span>
                  </label>
                  {errors.agreeToTerms && (
                    <p>{errors.agreeToTerms}</p>
                  )}
              </div>
              )}

              { (type !== DialogType.Verify && (!errors.criticalError)) && (
                <div className="human">
                    <altcha-widget 
                        ref={this.altchaRef}
                        challengeurl="/v1/challenge"
                        expire={Math.floor(Date.now() / 1000) + 500}
                        workers={1}          // fewer workers for old CPUs
                        delay={0}            // no artificial delay
                        maxnumber={200000}   // must match/align with your backend
                        onverified={(e) => { this.onVerified(e); }}></altcha-widget>
                </div>
              )}

              { this.renderSubmit() }
            </div>

            { this.renderSwitchMode() }
          </dialog>
        );
    }

    renderHeader() : React.JSX.Element
    {
        switch(this.state.type) {
            case DialogType.Login :
                return (
                    <div className="signin-heading">
                      <h1>Welcome Back</h1>
                      <p>Sign in to continue</p>
                    </div>
                );
            case DialogType.CreateAccount :
                return (
                    <div className="signin-heading">
                      <h1>Create Account</h1>
                      <p>Join us today</p>
                    </div>
                );
            case DialogType.Verify :
                return (
                    <div className="signin-heading">
                      <h1>Verify Account</h1>
                      <p>Ender code from email</p>
                    </div>
                );
            case DialogType.ForgotPassword :
                return (
                    <div className="signin-heading">
                      <h1>Reset password</h1>
                      <p>Enter email address and we will send you a password reset link.</p>
                    </div>
                );

            case DialogType.ChangePassword :
                return (
                    <div className="signin-heading">
                      <h1>Welcome Back</h1>
                      <p>Change your password</p>
                    </div>
                );

        }
    }

    renderPassword(password : string, errorPassword : string | undefined, showPassword : boolean, showForgotten : boolean) : React.JSX.Element
    {
        return (
          <div className="signin-field">
          <div className="field-label">
              <label htmlFor="password">
                Password
              </label>
              { this.state.type === DialogType.Login && (
              <button onClick={ () => { this.handleType(DialogType.ForgotPassword);} } className="forgot-password">Forgot password?</button>
              )}
          </div>

          <div className="input-with-icons">
            <Lock className="left"/>
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={password}
              onChange={this.handleInputChange}
              className={ errorPassword ? 'error' : ''}
              placeholder="Enter your password"
            />
            <button
              tabIndex={-1}
              type="button"
              onClick={this.togglePasswordVisibility}
              className="right"
            >
              {showPassword ? <EyeOff/> : <Eye/>}
            </button>
          </div>
          {errorPassword && (
            <p>{errorPassword}</p>
          )}
      </div>);
    }

    renderSubmit() : React.JSX.Element
    {
        const disabled = this.state.payload.length === 0 || this.state.errors.criticalError !== undefined
            || (this.state.type === DialogType.CreateAccount && !this.state.agreeToTerms);

        return (
            <button onClick={(e) => { this.handleSubmit(e) } } className="login-btn" disabled={ disabled }>
              { this.getSubmitText() }
            </button>
        );
    }

    renderSwitchMode() : React.JSX.Element | null
    {
        if (this.state.type === DialogType.Login) {
            return (
              <div className="switch">
                <p>Don't have an account?
                  <button onClick={ () => { this.handleType(DialogType.CreateAccount);} } className="create-account">
                    Create one
                  </button>
                </p>
              </div>
            );
        }
        else if (this.state.type === DialogType.CreateAccount) {
            return (
              <div className="switch">
                <p>Already have an account?
                  <button onClick={ () => { this.handleType(DialogType.Login); } } className="create-account">
                    Sign in
                  </button>
                </p>
              </div>
            );
        }

        return null;
    }

    renderText(text: string): React.JSX.Element 
    {
      return (
        <>
          {text.split('\n').map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </>
      );
    }

    removeTokenParam() 
    {
        const url = new URL(window.location.href);
        if (url.searchParams.has('token')) {
            url.searchParams.delete('token');
            window.history.replaceState({}, document.title, url.pathname + url.search);
        }
    }

    static async showChangePasswordDilog(token : string) : Promise<void>
    {
        return new Promise(() => {
            DialogManager.open(
                <AuthorizeDialog token={token}/>
            );
        });
    }

    static async showDialog(): Promise<void> 
    {
        return new Promise(() => {
            DialogManager.open(
                <AuthorizeDialog/>
            );
        });
    }

    onVerified(e : CustomEvent)
    {
        console.log("Verified");
        this.setState( {
            payload : e.detail.payload
        });
    }

    validateForm()
    {
        const { email, password, repeatPassword } = this.state;
        const errors : ErrorInfo = {};

        if (!email) {
            errors.email = 'Email is required';
        } 
        else if (!/\S+@\S+\.\S+/.test(email)) {
            errors.email = 'Email is invalid';
        }

        if (this.state.type === DialogType.CreateAccount || this.state.type === DialogType.Login) {
            if (!password) {
                errors.password = 'Password is required';
            } 
            else if (password.length < 6) {
                errors.password = 'Password must be at least 6 characters';
            }
        }

        if (this.state.type === DialogType.CreateAccount) {
            if (password !== repeatPassword) {
                errors.repeatPassword = 'Passwords does not match';
            }
            if (!this.state.agreeToTerms) {
                errors.agreeToTerms = 'You must agree to the Terms of Service and Privacy Policy';
            }
        }

        return errors;
    };
}
